import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';

const SERVICE_PATHS = ['/services/', '/product-visualization/', '/architectural-rendering/', '/game-assets/', '/3d-animation/'];

const Navigation = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/services/') return SERVICE_PATHS.some((p) => location.pathname.startsWith(p));
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Destinations plus one action. Services has to be here: the footer renders
  // only on blog posts, so without it the service pages had no route in from
  // the rest of the site, and Google never fetched them.
  const navItems = [
    { path: '/', label: 'Work' },
    { path: '/services/', label: 'Services' },
    { path: '/videos/', label: 'Videos' },
    { path: '/blog/', label: 'Blog' },
    { path: '/about/', label: 'About' },
  ];

  const linkClass = (path) => `text-[11.5px] font-medium tracking-[0.16em] uppercase pb-0.5 border-b transition-colors duration-300 ${
    isActive(path) ? 'text-white border-white' : 'text-white/55 border-transparent hover:text-white/85'
  }`;

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 bg-[#0B0B0B] border-b border-white/[0.07]"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
    >
      {/* Same container token as every content section — the wordmark and the h1 share a left edge */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="font-display text-[11.5px] sm:text-[13px] font-extrabold tracking-[0.18em] sm:tracking-[0.2em] text-white hover:text-white/80 transition-colors whitespace-nowrap shrink-0 py-1.5">
            GIANLUCA GATTO
          </Link>

          {/* Desktop. The full bar needs about 817px, so narrower tablets get the mobile layout. */}
          <div className="hidden min-[840px]:flex items-center gap-4 lg:gap-7">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} className={linkClass(item.path)}>
                {item.label}
              </Link>
            ))}
            <Link
              to="/contact/"
              className="ml-1 bg-white text-[#0B0B0B] text-[11.5px] font-semibold tracking-[0.14em] uppercase px-3 lg:px-5 py-2.5 whitespace-nowrap hover:bg-white/90 transition-colors"
            >
              Start a commission
            </Link>
          </div>

          {/* Mobile: the primary action stays visible — it should never live behind the menu */}
          <div className="flex min-[840px]:hidden items-center gap-3">
            <Link
              to="/contact/"
              className="bg-white text-[#0B0B0B] text-[10.5px] font-semibold tracking-[0.1em] uppercase px-3 py-2 whitespace-nowrap"
            >
              Commission
            </Link>
            <button
              className="text-white/70 hover:text-white transition-colors p-1"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              className="min-[840px]:hidden pt-4 pb-2 flex flex-col gap-3 items-start"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.13, ease: "easeOut" }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`${linkClass(item.path)} py-1.5`}
                >
                  {item.label}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navigation;
